import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy
} from '@angular/core';
import { IHeaderAngularComp } from 'ag-grid-angular';
import { IHeaderParams, SortDirection } from 'ag-grid-community';
import { TableColumn } from '../interfaces';

export interface TableHeaderParams extends IHeaderParams {
    tableColumn?: TableColumn;
    centered?: boolean;
}

/**
 * Column header for the data table, matching the static-table heading: the
 * column name with its sort indicator, the description (or name) as a
 * tooltip, an optional enhanced help entry named `<key>-column`, and the
 * `table-sort` analytics event. The grid's own column drag and resize
 * handling is untouched.
 */
@Component({
    selector: 'table-header',
    template: `
        <div
            *ngIf="col != null"
            class="table-header"
            [class.sortable]="sortable"
            [class.centered-heading]="centered"
            (click)="onSort()"
            [tooltip]="col.description || col.name"
            placement="top"
            [enhancedHelp]="col.key + '-column'"
            [enhancedHelpOptional]="true"
            [enhancedHelpShowOnActivate]="false"
            angulartics2On="click"
            angularticsAction="table-sort"
        >
            <span class="table-header-name">{{ col.name }}</span>
            <ng-container *ngIf="sortable">
                <span *ngIf="sort === 'desc'" class="fa fa-sort-down"></span>
                <span *ngIf="sort === 'asc'" class="fa fa-sort-up"></span>
                <span *ngIf="sort == null" class="fal fa-sort"></span>
            </ng-container>
        </div>
    `,
    styles: [
        `
            :host {
                display: flex;
                align-items: center;
                width: 100%;
                height: 100%;
                min-width: 0;
            }
            .table-header {
                display: flex;
                align-items: center;
                gap: 4px;
                width: 100%;
                min-width: 0;
                white-space: nowrap;
            }
            .table-header.sortable {
                cursor: pointer;
            }
            .table-header.centered-heading {
                justify-content: center;
            }
            .table-header-name {
                overflow: hidden;
                text-overflow: ellipsis;
            }
        `
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TableHeaderComponent implements IHeaderAngularComp, OnDestroy {
    params: TableHeaderParams;
    col?: TableColumn;
    sortable = false;
    centered = false;
    sort: SortDirection | null = null;

    // Grid column events arrive outside Angular's zone, so render explicitly
    private readonly onSortChanged = () => {
        this.readSort();
        this.changeDetector.detectChanges();
    };

    constructor(private changeDetector: ChangeDetectorRef) {}

    agInit(params: TableHeaderParams) {
        this.params = params;
        this.col = params.tableColumn;
        this.sortable = params.enableSorting === true;
        this.centered = params.centered === true;
        params.column.addEventListener('sortChanged', this.onSortChanged);
        this.readSort();
    }

    refresh(params: TableHeaderParams) {
        this.params.column.removeEventListener(
            'sortChanged',
            this.onSortChanged
        );
        this.agInit(params);
        return true;
    }

    ngOnDestroy() {
        this.params?.column.removeEventListener(
            'sortChanged',
            this.onSortChanged
        );
    }

    onSort() {
        if (this.sortable) {
            this.params.progressSort(false);
        }
    }

    private readSort() {
        this.sort = this.params.column.getSort() ?? null;
    }
}
